<template>
  <button :id="triggerId" ref="triggerRef" type="button" :class="className" :aria-expanded="isOpen" aria-haspopup="menu"
    class="cursor-pointer" @click="toggleMenu" @keydown.down.prevent="handleTriggerKeydown"
    @keydown.up.prevent="handleTriggerKeydown">
    <slot name="trigger">
      <span>Menu</span>
      <svg class="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
        aria-hidden="true">
        <path fill-rule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
          clip-rule="evenodd" />
      </svg>
    </slot>
  </button>

  <Teleport to="body" v-if="isOpen">
    <div ref="menuRef"
      class="absolute z-50 bg-card shadow-lg border rounded max-h-[70vh] overflow-auto select-none min-w-25 whitespace-normal animate-[popdown_0.2s] popover__menu flex flex-col"
      role="menu" :aria-labelledby="triggerId" tabindex="-1" :style="menuStyles" @keydown="handleMenuKeydown"
      @click.capture="handleMenuClickCapture">
      <slot :close="() => isOpen = false" />
    </div>
  </Teleport>
</template>
<style>
.popover__menu a {
  padding: 0.5rem 1rem;
}

.popover__menu a:hover {
  background: var(--color-auto);
}
</style>

<script setup lang="ts">
  import {
    autoUpdate,
    computePosition,
    flip,
    offset,
    type Placement,
    shift,
  } from "@floating-ui/dom";
  import {
    cloneVNode,
    computed,
    nextTick,
    onBeforeUpdate,
    onMounted,
    onUnmounted,
    ref,
    useId,
    useSlots,
    watch,
  } from "vue";

  const slots = useSlots();

  const isOpen = ref(false);
  const triggerRef = ref();
  const menuRef = ref();
  const itemRefs = ref([]);
  const triggerId = `popup-trigger-${useId()}`;

  // Floating UI state
  const menuStyles = ref({});
  let cleanupAutoUpdate: (() => void) | null;

  // Props (you can define them explicitly if using defineProps)
  const { placement = "bottom-center", offsetDistance = 4 } = defineProps<{
    className?: string;
    placement?: Placement;
    offsetDistance?: number;
  }>();

  onBeforeUpdate(() => {
    itemRefs.value = [];
  });

  const menuItems = computed(() => {
    const children = slots.default ? slots.default() : [];
    return children.map((vnode) => {
      const cloned = cloneVNode(vnode);
      cloned.props = { ...cloned.props, tabindex: "-1" };
      return cloned;
    });
  });

  // Toggle menu open/close
  const toggleMenu = () => {
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
      nextTick(() => {
        updatePosition();
        // setFocusOnItem(0);
      });
    } else {
      stopAutoUpdate();
    }
  };

  // Open menu and focus item at given index
  const openMenuAndFocus = (index: number) => {
    if (!isOpen.value) {
      isOpen.value = true;
      nextTick(() => {
        updatePosition();
        setFocusOnItem(index);
      });
    } else {
      setFocusOnItem(index);
    }
  };

  const setFocusOnItem = (index: number) => {
    nextTick(() => {
      const items = menuRef.value?.querySelectorAll("a");
      items[index].focus();
    });
  };

  const handleTriggerKeydown = (e: KeyboardEvent) => {
    const key = e.key;
    if (key === "ArrowDown") {
      e.preventDefault();
      openMenuAndFocus(0);
    } else if (key === "ArrowUp") {
      e.preventDefault();
      openMenuAndFocus(menuItems.value.length - 1);
    }
  };

  let currentIndex = 0;
  let focusedIndex = 0;
  // Handle keydown inside the menu
  const handleMenuKeydown = (e) => {
    const key = e.key;
    const items = menuRef.value?.querySelectorAll("a") ?? [];
    if (items.length === 0) return;
    switch (key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusOnItem((currentIndex + 1) % items.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusOnItem((currentIndex - 1 + items.length) % items.length);
        break;
      case "Home":
        e.preventDefault();
        setFocusOnItem(0);
        break;
      case "End":
        e.preventDefault();
        setFocusOnItem(items.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (currentIndex !== -1) {
          if (items[currentIndex].name === "A") {
            items[currentIndex].click();
          }
          closeMenu();
        }
        break;
      case "Escape":
        e.preventDefault();
        closeMenu();
        break;
    }
  };

  // Capture click inside menu – if an anchor is clicked, close the menu
  const handleMenuClickCapture = (e) => {
    if (e.target.closest("a")) {
      closeMenu();
    }
  };

  // Close menu and return focus to trigger
  const closeMenu = () => {
    isOpen.value = false;
    focusedIndex = -1;
    triggerRef.value?.focus();
    stopAutoUpdate();
  };

  const handleClickOutside = (e) => {
    if (!isOpen.value) return;
    const trigger = triggerRef.value;
    const menu = menuRef.value;
    if (!trigger || !menu) return;
    if (!trigger.contains(e.target) && !menu.contains(e.target)) {
      closeMenu();
    }
  };

  // Floating UI positioning
  const updatePosition = async () => {
    if (!triggerRef.value || !menuRef.value) return;

    const { x, y } = await computePosition(triggerRef.value, menuRef.value, {
      placement: placement,
      middleware: [offset(offsetDistance), flip(), shift({ padding: 5 })],
    });

    menuStyles.value = {
      top: `${y}px`,
      left: `${x}px`,
    };
  };

  const startAutoUpdate = () => {
    if (!triggerRef.value || !menuRef.value) return;
    if (cleanupAutoUpdate) cleanupAutoUpdate();
    cleanupAutoUpdate = autoUpdate(
      triggerRef.value,
      menuRef.value,
      updatePosition,
      { animationFrame: true },
    );
  };

  const stopAutoUpdate = () => {
    if (cleanupAutoUpdate) {
      cleanupAutoUpdate();
      cleanupAutoUpdate = null;
    }
  };

  onMounted(() => {
    document.addEventListener("mousedown", handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener("mousedown", handleClickOutside);
    stopAutoUpdate();
  });

  watch(isOpen, (newVal) => {
    if (newVal) {
      nextTick(() => {
        startAutoUpdate();
      });
    } else {
      stopAutoUpdate();
      focusedIndex = -1;
    }
  });
</script>